<template>
  <form @submit.prevent="onSubmit" class="checkout">
    <h3>New Card Details</h3>

    <NanInput
      v-model="name"
      label="Name on card"
      required
      :validation="$v.name"
      autocomplete="ccname"
      class="mb-3"
    />

    <span>
      Credit or debit card
    </span>
    <div class="mb-3">
      <!-- Stripe Elements card -->
      <div ref="card"></div>

      <!-- Stripe Elements errors. -->
      <div v-if="cardErrors" class="text-danger" role="alert">
        <span>{{ cardErrors }}</span>
      </div>
    </div>

    <template v-if="requireAddress">
      <h3>Billing Address</h3>

      <NanInput
        v-model="street"
        label="Street"
        required
        :validation="$v.address"
        autocomplete="street-address"
        class="mb-3"
      />

      <NanInput
        v-model="city"
        label="City"
        autocomplete="locality"
        class="mb-3"
      />

      <NanInput
        v-model="code"
        label="Postal Code"
        required
        :validation="$v.code"
        autocomplete="postal-code"
        class="mb-3"
      />

      <NanInput
        v-model="country"
        type="select"
        required
        :validation="$v.country"
        :options="countries"
        label="Country"
        autocomplete="country"
        class="mb-3"
      />
    </template>

    <b-btn
      v-if="!hideSubmit"
      :class="{ disabled: $v.$invalid }"
      variant="primary"
      type="submit"
    >
      Submit
    </b-btn>
  </form>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import countries from "@/data/countries";

const countryNames = countries.map(c => c.name);
countryNames.unshift("");

export default {
  mixins: [validationMixin],

  props: {
    requireAddress: {
      type: Boolean,
      default: true
    },
    hideSubmit: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    name: "",
    street: "",
    city: "",
    code: "",
    country: "",
    countries: countryNames,
    cardComplete: false,
    cardErrors: "",
    isLoading: false,
    error: null
  }),

  computed: {
    isValid() {
      return this.cardComplete && !this.$v.$invalid;
    }
  },

  beforeMount() {
    // Setup the Stripe Elements
    // eslint-disable-next-line
    this._stripe = Stripe(process.env.VUE_APP_STRIPE);
    const elements = this._stripe.elements();

    // https://stripe.com/docs/stripe.js#element-options
    const options = {
      classes: {
        base: "form-control",
        focus: "form-control--focused"
      },
      hidePostalCode: true
    };

    this._card = elements.create("card", options);

    this._card.on("change", event => {
      const { error } = event;
      if (error) {
        this.cardErrors = error.message;
      } else {
        this.cardErrors = "";
      }
      this.cardComplete = event.complete;
    });
  },

  mounted() {
    // Add Stripe Element to real element
    const el = document.createElement("div");
    this._card.mount(el);

    // this.$children cannot be used because it expects a VNode :(
    this.$refs.card.appendChild(el);
  },

  methods: {
    clear() {
      this._card.clear();
      this.street = "";
      this.city = "";
      this.code = "";
      this.country = "";
    },

    async onSubmit() {
      if (!this.isValid) {
        this.$v.touch();
        return;
      }

      try {
        this.isLoading = true;

        const { name, street, city, code, country } = this;

        // https://stripe.com/docs/stripe.js#stripe-create-token
        const data = await this._stripe.createToken(this._card, {
          name,
          address_line1: street,
          address_city: city,
          address_zip: code,
          address_country: country
        });

        if (data.error) {
          throw data.error;
        }

        const tokenId = data.token.id;
        const cardInfo = {
          id: data.token.card.id,
          brand: data.token.card.brand,
          exp_month: data.token.card.exp_month,
          exp_year: data.token.card.exp_year,
          funding: data.token.card.funding,
          last4: data.token.card.last4
        };

        this.$emit("success", {
          tokenId,
          cardInfo,
          street,
          city,
          code,
          country
        });

        this.clear();
      } catch (err) {
        console.warn(err);
        this.$emit("error", err);
      } finally {
        this.isLoading = false;
      }
    }
  },

  watch: {
    isValid: function(isValid) {
      this.$emit("validationChange", isValid);
    },

    isLoading: function(isLoading) {
      this.$emit("loadingChange", isLoading);
    }
  },

  beforeDestroy() {
    this._card.unmount();
    this._card.destroy();
  },

  validations: () => ({
    name: { required },
    street: { required },
    // city: { required },
    code: { required },
    country: { required }
  })
};
</script>

<style lang="scss">
.checkout {
  .form-control--focused {
    border-color: #48cbfd;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(2, 142, 196, 0.25);
  }
}
</style>
